import { useMemo } from 'react';
import { useUniverseSecurityItems } from '@biasafe.queries/securities-universes-security-items/securities-universes-security-items';
import { STATIC_COUNTRIES } from '../config/constants/staticClassifications';
import { universeSecuritiesQuery } from '@/shared/config/queryDefaults';
import { useUniverseAsOfDate } from '../../RebalancingSchedule/hooks/useLatestReconstitutionDate';

/**
 * The country catalogue is STATIC, always. The live securities only say which
 * countries are POPULATED today, and that is an annotation, not a filter.
 *
 * This hook used to BUILD the list from the current securities and keep the
 * static one as an offline fallback. That made four countries unselectable —
 * Cayman Islands, Morocco, Pakistan and Russia. Russia shows why it matters:
 * Russian constituents sat in the index until 2022-03-18, so a backtest over
 * 2010–2022 legitimately covers them, and the user could not exclude them.
 * Nothing errored; the option simply was not there.
 *
 * Same reasoning as the sector / industry catalogues: a backtest is
 * point-in-time, so the picker must offer everything the taxonomy has ever
 * held, and what the universe contains today guides rather than restricts.
 */
export function useCountriesFromSecurities() {
  // Step 2's lagged data-as-of date (schedule.recon_lag_map) — the date the
  // ENGINE actually reads the universe at, not the recon date itself. Resolving
  // step-1 data here at the same instant makes the step-1 and step-2 counts
  // agree; it is also part of the queryKey, so changing the schedule refetches
  // instead of serving the previous date from cache.
  const asOf = useUniverseAsOfDate();
  // Pass `as_of` in the REQUEST PARAMS, not only in the queryKey: universe-
  // SecuritiesQuery keys this on the date, so leaving params `undefined` fetched
  // the LATEST snapshot yet cached it under the dated key — poisoning the very
  // entry the point-in-time hooks (useFilteredSecurities, usePointInTime) read.
  const { data: securities = [], isLoading } = useUniverseSecurityItems(
    asOf ? { as_of: asOf } : undefined,
    {
      query: {
        ...universeSecuritiesQuery(asOf),
      },
    },
  );

  /** Countries the selected universe holds at the reconstitution date. */
  const inUniverseCountries = useMemo(
    () => new Set(securities.map((s) => s.country)),
    [securities],
  );

  return { countries: STATIC_COUNTRIES, inUniverseCountries, isLoading };
}
